import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { usePageHeaderStore } from '../../stores/pageHeader';
import {
  User,
  Calendar,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Check,
  X,
  Edit3,
  Trash2,
  Plus,
  Save,
  CheckCircle,
  CheckCircle2,
  Target,
  Paperclip,
  Eye,
  Settings,
  AlertCircle,
  Sparkles,
  FileOutput,
  FileText,
  Hand,
  Book,
  Loader2,
  Upload,
  PenTool,
  Link2
} from 'lucide-react';
import { useState, useEffect } from 'react';
import clsx from 'clsx';

// ============================================
// Types
// ============================================

interface EnhancedAC {
  id: string;
  text: string;
  origin: string;
  reviewStatus?: 'draft' | 'approved' | 'rejected' | 'edited';
  editedText?: string;
  tags?: string[];
  format?: 'simple' | 'given-when-then' | 'use-cases';
  priority?: string;
  effort?: string;
  rationale?: string;
  given?: string;
  when?: string;
  then?: string;
  useCaseName?: string;
  actor?: string;
  useCasePreconditions?: string[];
  mainFlow?: string[];
  alternativeFlows?: Array<{ condition: string; steps: string[] }>;
  postconditions?: string[];
  useCaseBusinessRules?: string[];
  citationsUsed?: string[];
  dependencies?: string[];
}

interface TextItem {
  id: string;
  text: string;
  origin: 'manual' | 'ai';
  reviewStatus?: 'draft' | 'approved' | 'rejected' | 'edited';
  editedText?: string;
  isEdited?: boolean;
}

interface SelectedElement {
  id: string;
  type?: string;
  label?: string;
  selector?: string;
  bounds?: { x: number; y: number; width: number; height: number };
  meta?: {
    type?: string;
    label?: string;
    tagName?: string;
    text?: string;
    placeholder?: string;
    value?: string;
    state?: string;
    [key: string]: unknown;
  };
  notes?: string;
  pageUrl?: string;
}

interface Screenshot {
  id: string;
  imageUrl?: string;
  dataUrl?: string;
  type?: 'current' | 'future' | 'general';
  capturedAt?: string;
  pageUrl?: string;
}

interface Attachment {
  id: string;
  name: string;
  type: string;
  size: number;
  dataUrl?: string;
  url?: string;
}

interface AgentMeta {
  acConfig?: {
    outputFormat: string;
    framework: string;
    includeRAG: boolean;
  };
  futureState?: {
    autoText?: string;
    editedText?: string;
    confidence?: number;
  };
  preconditions?: TextItem[];
  trigger?: TextItem;
  businessRules?: TextItem[];
  outOfScope?: TextItem[];
  changeLog?: {
    markdown?: string;
    generatedAt?: string;
  };
  attachments?: Attachment[];
  [key: string]: unknown;
}

interface StoryDetail {
  id: string;
  projectId: string;
  epicId: string | null;
  epicTitle: string | null;
  title: string;
  status: string;
  priority: string | null;
  assignee: { id: string; email: string; fullName: string | null } | null;
  createdBy: { id: string; email: string; fullName: string | null } | null;
  userStory: {
    asA: string | null;
    iWant: string | null;
    soThat: string | null;
  };
  acceptanceCriteria: EnhancedAC[];
  selectedElements: SelectedElement[];
  currentState: {
    autoText: string | null;
    editedText: string | null;
    confidence: number | null;
    generatedAt: string | null;
  };
  wireframes: Array<{ id: string; imageUrl: string; createdAt?: string }>;
  evidence: {
    url: string | null;
    consoleSummary: string | null;
    networkSummary: string | null;
    domSnippets: string | null;
    screenshots: string[] | Screenshot[];
  };
  agentMeta: AgentMeta;
  payloadVersion?: number;
  createdAt: string;
  updatedAt: string;
}

interface ProjectInfo {
  id: string;
  name: string;
  key: string;
  orgId: string;
  orgName: string;
}

const STATUS_OPTIONS = [
  'DRAFT', 'READY', 'ENRICHED', 'WIREFRAME_READY',
  'GENERATED', 'IN_REVIEW', 'APPROVED', 'REJECTED', 'DONE'
];

// ============================================
// Section Header Component (Extension-like)
// ============================================

function SectionHeader({ 
  title, 
  icon: Icon, 
  count,
  actions,
  className
}: { 
  title: string; 
  icon: React.ElementType;
  count?: number | string;
  actions?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={clsx('flex items-center gap-2 mb-3', className)}>
      <Icon className="w-4 h-4 text-neutral-500" />
      <h3 className="text-sm font-semibold text-neutral-900">{title}</h3>
      {count !== undefined && (
        <span className="px-1.5 py-0.5 text-xs font-medium bg-neutral-100 text-neutral-600 rounded-full">
          {count}
        </span>
      )}
      {actions && <div className="ml-auto flex items-center gap-1">{actions}</div>}
    </div>
  );
}

// ============================================
// Compact Section Card (Extension-like)
// ============================================

function SectionCard({ 
  children, 
  className 
}: { 
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={clsx(
      'bg-white border border-neutral-200 rounded-lg p-3',
      className
    )}>
      {children}
    </div>
  );
}

// ============================================
// Editable User Story Component
// ============================================

function EditableUserStory({ 
  userStory, 
  onSave 
}: { 
  userStory: StoryDetail['userStory'];
  onSave: (data: StoryDetail['userStory']) => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [asA, setAsA] = useState(userStory.asA || '');
  const [iWant, setIWant] = useState(userStory.iWant || '');
  const [soThat, setSoThat] = useState(userStory.soThat || '');

  useEffect(() => {
    setAsA(userStory.asA || '');
    setIWant(userStory.iWant || '');
    setSoThat(userStory.soThat || '');
  }, [userStory]);

  const handleSave = () => {
    onSave({ asA, iWant, soThat });
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="space-y-2">
        <div>
          <label className="text-xs text-neutral-500 mb-1 block">As a</label>
          <input
            type="text"
            className="input text-sm"
            value={asA}
            onChange={(e) => setAsA(e.target.value)}
            placeholder="user role..."
          />
        </div>
        <div>
          <label className="text-xs text-neutral-500 mb-1 block">I want</label>
          <textarea
            className="input text-sm resize-none"
            rows={2}
            value={iWant}
            onChange={(e) => setIWant(e.target.value)}
            placeholder="action/feature..."
          />
        </div>
        <div>
          <label className="text-xs text-neutral-500 mb-1 block">So that</label>
          <textarea
            className="input text-sm resize-none"
            rows={2}
            value={soThat}
            onChange={(e) => setSoThat(e.target.value)}
            placeholder="benefit/goal..."
          />
        </div>
        <div className="flex gap-2 pt-1">
          <button className="btn btn-primary btn-sm" onClick={handleSave}>
            <Save className="w-3.5 h-3.5" /> Save
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => setIsEditing(false)}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="group relative">
      <button 
        className="absolute top-0 right-0 p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-neutral-100 transition-all"
        onClick={() => setIsEditing(true)}
      >
        <Edit3 className="w-3.5 h-3.5 text-neutral-400" />
      </button>
      {userStory.asA || userStory.iWant ? (
        <div className="space-y-1.5 text-sm">
          {userStory.asA && (
            <p className="text-neutral-700"><span className="text-neutral-400">As a</span> {userStory.asA}</p>
          )}
          {userStory.iWant && (
            <p className="text-neutral-700"><span className="text-neutral-400">I want</span> {userStory.iWant}</p>
          )}
          {userStory.soThat && (
            <p className="text-neutral-700"><span className="text-neutral-400">So that</span> {userStory.soThat}</p>
          )}
        </div>
      ) : (
        <p className="text-neutral-400 text-sm italic">No user story defined. Click to add.</p>
      )}
    </div>
  );
}

// ============================================
// Elements List Component (Extension-like)
// ============================================

// ============================================
// Scope Item Component (with color coding and approval)
// ============================================

function ScopeItem({ 
  item, 
  type,
  onApprove,
  onEdit, 
  onDelete 
}: { 
  item: TextItem;
  type: 'precondition' | 'trigger' | 'business-rule' | 'out-of-scope';
  onApprove: () => void;
  onEdit: (text: string) => void;
  onDelete: () => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [text, setText] = useState(item.editedText || item.text);

  const isApproved = item.reviewStatus === 'approved';
  const isEditedByUser = item.isEdited || (item.editedText && item.editedText !== item.text);

  const borderClasses = {
    'precondition': 'border-l-blue-400',
    'trigger': 'border-l-purple-400',
    'business-rule': 'border-l-green-400',
    'out-of-scope': 'border-l-red-400',
  };

  if (isEditing) {
    return (
      <div className="flex items-start gap-2">
        <input
          type="text"
          className="input text-sm flex-1"
          value={text}
          onChange={(e) => setText(e.target.value)}
          autoFocus
        />
        <button
          className="btn btn-primary btn-sm"
          onClick={() => { onEdit(text); setIsEditing(false); }}
        >
          <Check className="w-3 h-3" />
        </button>
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => setIsEditing(false)}
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    );
  }

  return (
    <div className={clsx(
      'flex items-center gap-3 px-3 py-2.5 rounded-lg bg-neutral-50 border border-neutral-200 border-l-4 text-sm group transition-all',
      borderClasses[type],
      isApproved && 'ring-2 ring-success-500/30'
    )}>
      {/* Approve button */}
      <button
        className={clsx(
          'w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0 transition-colors',
          isApproved 
            ? 'bg-success-500 text-white' 
            : 'bg-white border border-neutral-300 text-neutral-400 hover:bg-success-100 hover:text-success-600 hover:border-success-300'
        )}
        onClick={onApprove}
        title={isApproved ? 'Unapprove' : 'Approve'}
      >
        {isApproved && <Check className="w-3 h-3" />}
      </button>
      
      <span className="flex-1 min-w-0 text-neutral-700">{item.editedText || item.text}</span>
      
      {/* Badges */}
      <div className="flex items-center gap-1">
        {isEditedByUser && (
          <span title="Edited by user" className="text-amber-600">
            <Hand className="w-3.5 h-3.5" />
          </span>
        )}
        {item.origin === 'ai' && (
          <span title="AI generated"><Sparkles className="w-3.5 h-3.5 text-accent-400 shrink-0" /></span>
        )}
      </div>
      
      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          className="p-1 rounded hover:bg-neutral-200 text-neutral-400 hover:text-neutral-600 transition-colors"
          onClick={() => setIsEditing(true)}
          title="Edit"
        >
          <Edit3 className="w-3.5 h-3.5" />
        </button>
        <button
          className="p-1 rounded hover:bg-neutral-200 text-neutral-400 hover:text-neutral-600 transition-colors"
          onClick={onDelete}
          title="Delete"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// ============================================
// Scope & Rules Section with Filter
// ============================================

function ScopeRulesSection({
  preconditions,
  trigger,
  businessRules,
  outOfScope,
  onUpdate
}: {
  preconditions: TextItem[];
  trigger?: TextItem;
  businessRules: TextItem[];
  outOfScope: TextItem[];
  onUpdate: (data: {
    preconditions?: TextItem[];
    trigger?: TextItem;
    businessRules?: TextItem[];
    outOfScope?: TextItem[];
  }) => void;
}) {
  const [newItemType, setNewItemType] = useState<string | null>(null);
  const [newItemText, setNewItemText] = useState('');
  const [filterStatus, setFilterStatus] = useState<'approved' | 'all'>('approved');

  const totalCount = preconditions.length + (trigger ? 1 : 0) + businessRules.length + outOfScope.length;
  
  // Count approved items (includes 'edited' since it was previously approved)
  const approvedCount = [
    ...preconditions.filter(p => p.reviewStatus === 'approved' || p.reviewStatus === 'edited'),
    ...businessRules.filter(r => r.reviewStatus === 'approved' || r.reviewStatus === 'edited'),
    ...outOfScope.filter(o => o.reviewStatus === 'approved' || o.reviewStatus === 'edited'),
    ...((trigger?.reviewStatus === 'approved' || trigger?.reviewStatus === 'edited') ? [trigger] : [])
  ].length;
  
  // Filter items based on status (approved includes 'edited' since it was previously approved)
  const filterItems = (items: TextItem[]) => {
    if (filterStatus === 'all') return items;
    return items.filter(item => item.reviewStatus === 'approved' || item.reviewStatus === 'edited');
  };
  
  const filteredPreconditions = filterItems(preconditions);
  const filteredBusinessRules = filterItems(businessRules);
  const filteredOutOfScope = filterItems(outOfScope);
  const showTrigger = filterStatus === 'all' || trigger?.reviewStatus === 'approved' || trigger?.reviewStatus === 'edited';
  
  // Toggle approval status for an item
  const handleApprove = (itemId: string, itemType: 'preconditions' | 'trigger' | 'businessRules' | 'outOfScope') => {
    if (itemType === 'trigger' && trigger) {
      const newStatus = trigger.reviewStatus === 'approved' ? 'draft' : 'approved';
      onUpdate({ trigger: { ...trigger, reviewStatus: newStatus } });
    } else if (itemType === 'preconditions') {
      onUpdate({ 
        preconditions: preconditions.map(p => p.id === itemId 
          ? { ...p, reviewStatus: p.reviewStatus === 'approved' ? 'draft' : 'approved' } 
          : p
        ) 
      });
    } else if (itemType === 'businessRules') {
      onUpdate({ 
        businessRules: businessRules.map(r => r.id === itemId 
          ? { ...r, reviewStatus: r.reviewStatus === 'approved' ? 'draft' : 'approved' } 
          : r
        ) 
      });
    } else if (itemType === 'outOfScope') {
      onUpdate({ 
        outOfScope: outOfScope.map(o => o.id === itemId 
          ? { ...o, reviewStatus: o.reviewStatus === 'approved' ? 'draft' : 'approved' } 
          : o
        ) 
      });
    }
  };

  const handleAddItem = () => {
    if (!newItemType || !newItemText.trim()) return;
    
    const newItem: TextItem = {
      id: `${newItemType}_${Date.now()}`,
      text: newItemText.trim(),
      origin: 'manual',
      reviewStatus: 'draft'
    };

    switch (newItemType) {
      case 'precondition':
        onUpdate({ preconditions: [...preconditions, newItem] });
        break;
      case 'trigger':
        onUpdate({ trigger: newItem });
        break;
      case 'business-rule':
        onUpdate({ businessRules: [...businessRules, newItem] });
        break;
      case 'out-of-scope':
        onUpdate({ outOfScope: [...outOfScope, newItem] });
        break;
    }
    
    setNewItemType(null);
    setNewItemText('');
  };

  return (
    <SectionCard>
      <div className="flex items-center justify-between mb-3">
        <SectionHeader 
          title="Scope & rules" 
          icon={Target} 
          count={`${approvedCount}/${totalCount}`}
        />
        
        {/* Filter toggle */}
        <div className="flex items-center gap-1 bg-neutral-100 rounded-lg p-0.5">
          <button
            className={clsx(
              'px-2.5 py-1 text-xs rounded-md transition-colors',
              filterStatus === 'approved' 
                ? 'bg-white text-neutral-800 shadow-sm' 
                : 'text-neutral-500 hover:text-neutral-700'
            )}
            onClick={() => setFilterStatus('approved')}
          >
            Approved
          </button>
          <button
            className={clsx(
              'px-2.5 py-1 text-xs rounded-md transition-colors',
              filterStatus === 'all' 
                ? 'bg-white text-neutral-800 shadow-sm' 
                : 'text-neutral-500 hover:text-neutral-700'
            )}
            onClick={() => setFilterStatus('all')}
          >
            All
          </button>
        </div>
      </div>
      
      {/* Add buttons */}
      <div className="flex flex-wrap gap-2 mb-4">
        {['precondition', 'trigger', 'business-rule', 'out-of-scope'].map((type) => (
          <button
            key={type}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-neutral-200 bg-neutral-50 text-neutral-600 hover:bg-neutral-100 hover:border-neutral-300 transition-colors"
            onClick={() => setNewItemType(type)}
            disabled={type === 'trigger' && !!trigger}
          >
            <Plus className="w-3 h-3" />
            {type.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
          </button>
        ))}
      </div>

      {/* New item input */}
      {newItemType && (
        <div className="flex items-center gap-2 mb-4 p-2.5 bg-neutral-50 rounded-lg border border-neutral-200">
          <input
            type="text"
            className="input text-sm flex-1"
            placeholder={`Enter ${newItemType.replace('-', ' ')}...`}
            value={newItemText}
            onChange={(e) => setNewItemText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddItem()}
            autoFocus
          />
          <button className="btn btn-primary btn-sm" onClick={handleAddItem} disabled={!newItemText.trim()}>
            Add
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => { setNewItemType(null); setNewItemText(''); }}>
            Cancel
          </button>
        </div>
      )}

      {/* Grouped sections like extension - filtered */}
      <div className="space-y-4">
        {/* Preconditions */}
        {filteredPreconditions.length > 0 && (
          <div>
            <h4 className="text-xs font-medium text-neutral-500 mb-2">Preconditions</h4>
            <div className="space-y-1.5">
              {filteredPreconditions.map((item) => (
                <ScopeItem
                  key={item.id}
                  item={item}
                  type="precondition"
                  onApprove={() => handleApprove(item.id, 'preconditions')}
                  onEdit={(text) => onUpdate({ preconditions: preconditions.map(p => p.id === item.id ? { ...p, text, editedText: text, isEdited: true } : p) })}
                  onDelete={() => onUpdate({ preconditions: preconditions.filter(p => p.id !== item.id) })}
                />
              ))}
            </div>
          </div>
        )}
        
        {/* Trigger */}
        {showTrigger && trigger && (
          <div>
            <h4 className="text-xs font-medium text-neutral-500 mb-2">Trigger</h4>
            <div className="space-y-1.5">
              <ScopeItem
                item={trigger}
                type="trigger"
                onApprove={() => handleApprove(trigger.id, 'trigger')}
                onEdit={(text) => onUpdate({ trigger: { ...trigger, text, editedText: text, isEdited: true } })}
                onDelete={() => onUpdate({ trigger: undefined })}
              />
            </div>
          </div>
        )}
        
        {/* Business Rules */}
        {filteredBusinessRules.length > 0 && (
          <div>
            <h4 className="text-xs font-medium text-neutral-500 mb-2">Business Rules</h4>
            <div className="space-y-1.5">
              {filteredBusinessRules.map((item) => (
                <ScopeItem
                  key={item.id}
                  item={item}
                  type="business-rule"
                  onApprove={() => handleApprove(item.id, 'businessRules')}
                  onEdit={(text) => onUpdate({ businessRules: businessRules.map(r => r.id === item.id ? { ...r, text, editedText: text, isEdited: true } : r) })}
                  onDelete={() => onUpdate({ businessRules: businessRules.filter(r => r.id !== item.id) })}
                />
              ))}
            </div>
          </div>
        )}
        
        {/* Out of Scope */}
        {filteredOutOfScope.length > 0 && (
          <div>
            <h4 className="text-xs font-medium text-neutral-500 mb-2">Out of Scope</h4>
            <div className="space-y-1.5">
              {filteredOutOfScope.map((item) => (
                <ScopeItem
                  key={item.id}
                  item={item}
                  type="out-of-scope"
                  onApprove={() => handleApprove(item.id, 'outOfScope')}
                  onEdit={(text) => onUpdate({ outOfScope: outOfScope.map(o => o.id === item.id ? { ...o, text, editedText: text, isEdited: true } : o) })}
                  onDelete={() => onUpdate({ outOfScope: outOfScope.filter(o => o.id !== item.id) })}
                />
              ))}
            </div>
          </div>
        )}

        {totalCount === 0 && (
          <p className="text-neutral-400 text-sm text-center py-3">
            No scope items defined. Add preconditions, triggers, or rules above.
          </p>
        )}
      </div>
    </SectionCard>
  );
}

// ============================================
// AC Item Component (Extension-like)
// ============================================

function ACItem({ 
  ac, 
  index,
  onApprove,
  onReject,
  onEdit,
  onDelete,
  ragBundle
}: { 
  ac: EnhancedAC;
  index: number;
  onApprove: () => void;
  onReject: () => void;
  onEdit: (text: string) => void;
  onDelete: () => void;
  ragBundle?: RAGBundle;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(ac.editedText || ac.text);
  const [showCitationTooltip, setShowCitationTooltip] = useState(false);
  
  // Get citation texts for tooltip
  const citationDetails = ac.citationsUsed?.map(citationId => {
    const citation = ragBundle?.citations?.find(c => c.citationId === citationId);
    return citation ? {
      title: citation.doc.title,
      quote: citation.quote.text,
      confidence: citation.confidence,
    } : null;
  }).filter(Boolean) || [];

  const isApproved = ac.reviewStatus === 'approved' || ac.reviewStatus === 'edited';
  const isEditedByUser = ac.reviewStatus === 'edited' || (ac.editedText && ac.editedText !== ac.text);
  const isRejected = ac.reviewStatus === 'rejected';
  const isUseCase = ac.format === 'use-cases' && ac.useCaseName;
  const isGWT = ac.format === 'given-when-then' && ac.given && ac.when && ac.then;

  const handleSaveEdit = () => {
    onEdit(editText);
    setIsEditing(false);
  };

  // Priority color mapping
  const getPriorityClass = (priority?: string) => {
    if (!priority) return 'bg-neutral-100 text-neutral-600';
    if (priority.toLowerCase().includes('critical') || priority === 'P0') return 'bg-red-500 text-white';
    if (priority.toLowerCase().includes('must') || priority === 'P1') return 'bg-orange-500 text-white';
    if (priority.toLowerCase().includes('high')) return 'bg-orange-400 text-white';
    if (priority.toLowerCase().includes('medium') || priority === 'P2') return 'bg-yellow-500 text-white';
    return 'bg-neutral-200 text-neutral-700';
  };

  // Use Case rendering (always expanded like extension)
  if (isUseCase) {
    return (
      <div className={clsx(
        'rounded-lg border transition-all text-sm',
        isApproved ? 'bg-success-50/30 border-success-500/30' :
        isRejected ? 'bg-error-50/30 border-error-500/30 opacity-60' :
        'bg-white border-neutral-200'
      )}>
        {/* Use Case Header */}
        <div className="flex items-center gap-2 p-3 border-b border-neutral-100">
          <span className="text-xs font-mono text-neutral-500">UC-{index + 1}</span>
          {ac.priority && (
<span className={clsx('text-[10px] px-1.5 py-0.5 rounded font-medium', getPriorityClass(ac.priority))}>
                {ac.priority}
              </span>
          )}
          {ac.effort && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-600 font-medium">{ac.effort}</span>
          )}
          {ac.origin === 'ai' && (
            <Sparkles className="w-3.5 h-3.5 text-accent-500" />
          )}
          {isEditedByUser && (
            <span title="Edited by user" className="text-amber-600">
              <Hand className="w-3.5 h-3.5" />
            </span>
          )}
          
          <div className="flex-1" />
          
          {/* Approve button */}
          <button
            className={clsx(
              'p-1 rounded transition-colors',
              isApproved ? 'bg-success-500 text-white' : 'hover:bg-neutral-100 text-neutral-400'
            )}
            onClick={isApproved ? onReject : onApprove}
            title={isApproved ? 'Unapprove' : 'Approve'}
          >
            <Check className="w-4 h-4" />
          </button>
          <button
            className="p-1 rounded hover:bg-neutral-100 text-neutral-400 hover:text-neutral-600 transition-colors"
            onClick={() => setIsEditing(true)}
            title="Edit"
          >
            <Edit3 className="w-4 h-4" />
          </button>
          <button
            className="p-1 rounded hover:bg-error-50 text-neutral-400 hover:text-error-500 transition-colors"
            onClick={onDelete}
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
        
        {/* Use Case Content */}
        <div className="p-3">
          {isEditing ? (
            <div className="space-y-2">
              <textarea
                className="input text-sm resize-none w-full"
                rows={3}
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                autoFocus
              />
              <div className="flex gap-2">
                <button className="btn btn-primary btn-sm" onClick={handleSaveEdit}>Save</button>
                <button className="btn btn-secondary btn-sm" onClick={() => setIsEditing(false)}>Cancel</button>
              </div>
            </div>
          ) : (
            <>
              {/* Title and Actor */}
              <div className="flex items-baseline gap-2 mb-3">
                <h4 className="font-medium text-neutral-800">{ac.useCaseName}</h4>
                {ac.actor && (
                  <span className="text-xs text-neutral-400">Actor: {ac.actor}</span>
                )}
              </div>
              
              {/* Trigger */}
              {(ac as any).trigger && (
                <div className="mb-3">
                  <p className="text-[10px] text-neutral-400 font-medium tracking-wide mb-1">Trigger</p>
                  <p className="text-xs text-neutral-600">{(ac as any).trigger}</p>
                </div>
              )}
              
              {/* Preconditions */}
              {ac.useCasePreconditions && ac.useCasePreconditions.length > 0 && (
                <div className="mb-3">
                  <p className="text-[10px] text-neutral-400 font-medium tracking-wide mb-1">Preconditions</p>
                  <ul className="text-xs text-neutral-600 space-y-0.5">
                    {ac.useCasePreconditions.map((p, j) => (
                      <li key={j} className="flex items-start gap-1.5">
                        <span className="text-neutral-300">•</span>
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Main Flow */}
              {ac.mainFlow && ac.mainFlow.length > 0 && (
                <div className="mb-3">
                  <p className="text-[10px] text-neutral-400 font-medium tracking-wide mb-1">Main Flow</p>
                  <ol className="text-xs text-neutral-600 space-y-0.5">
                    {ac.mainFlow.map((step, j) => (
                      <li key={j} className="flex items-start gap-1.5">
                        <span className="text-neutral-400 font-mono">{j + 1}.</span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
              
              {/* Alternative Flows */}
              {ac.alternativeFlows && ac.alternativeFlows.length > 0 && (
                <div className="mb-3">
                  <p className="text-[10px] text-neutral-400 font-medium tracking-wide mb-1">Alternative Flows</p>
                  {ac.alternativeFlows.map((af, j) => (
                    <div key={j} className="mb-2">
                      <p className="text-xs text-amber-600 flex items-center gap-1">
                        <Sparkles className="w-3 h-3" />
                        {af.condition}
                      </p>
                      <ol className="text-xs text-neutral-600 ml-4 mt-0.5 space-y-0.5">
                        {af.steps.map((step, k) => (
                          <li key={k} className="flex items-start gap-1.5">
                            <span className="text-neutral-400 font-mono">{k + 1}.</span>
                            <span>{step}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Postconditions */}
              {ac.postconditions && ac.postconditions.length > 0 && (
                <div className="mb-3">
                  <p className="text-[10px] text-neutral-400 font-medium tracking-wide mb-1">Postconditions</p>
                  <ul className="text-xs text-neutral-600 space-y-0.5">
                    {ac.postconditions.map((p, j) => (
                      <li key={j} className="flex items-start gap-1.5">
                        <span className="text-neutral-300">•</span>
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Business Rules */}
              {(ac as any).useCaseBusinessRules && (ac as any).useCaseBusinessRules.length > 0 && (
                <div className="mb-3">
                  <p className="text-[10px] text-neutral-400 font-medium tracking-wide mb-1">Business Rules</p>
                  <ul className="text-xs text-neutral-600 space-y-0.5">
                    {(ac as any).useCaseBusinessRules.map((rule: string, j: number) => (
                      <li key={j} className="flex items-start gap-1.5">
                        <span className="text-neutral-300">•</span>
                        <span>{rule}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Out of Scope */}
              {(ac as any).outOfScope && (ac as any).outOfScope.length > 0 && (
                <div className="mb-3">
                  <p className="text-[10px] text-neutral-400 font-medium tracking-wide mb-1">Out of Scope</p>
                  <ul className="text-xs text-neutral-600 space-y-0.5">
                    {(ac as any).outOfScope.map((item: string, j: number) => (
                      <li key={j} className="flex items-start gap-1.5">
                        <span className="text-neutral-300">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Dependencies */}
              {ac.dependencies && ac.dependencies.length > 0 && (
                <div className="text-xs text-amber-600 flex items-center gap-1 mt-2 pt-2 border-t border-neutral-100">
                  <AlertCircle className="w-3 h-3" />
                  <span>Depends on {ac.dependencies.join(', ')}</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  // Simple AC or GWT format
  return (
    <div className={clsx(
      'rounded-lg border transition-all text-sm group',
      isApproved ? 'bg-success-50/30 border-success-500/30' :
      isRejected ? 'bg-error-50/30 border-error-500/30 opacity-60' :
      'bg-white border-neutral-200 hover:border-neutral-300'
    )}>
      <div className="flex items-start gap-2 p-2.5">
        <button
          className={clsx(
            'w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5 transition-colors',
            isApproved ? 'bg-success-500 text-white' :
            isRejected ? 'bg-error-500/20 text-error-500' :
            'bg-neutral-100 text-neutral-500 hover:bg-accent-100 hover:text-accent-600'
          )}
          onClick={isApproved ? onReject : onApprove}
          title={isApproved ? 'Unapprove' : 'Approve'}
        >
          {isApproved ? <Check className="w-3 h-3" /> : index + 1}
        </button>
        
        <div className="flex-1 min-w-0">
          {/* Badges */}
          <div className="flex items-center gap-1.5 mb-1 flex-wrap">
            {isGWT && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-accent-100 text-accent-700 font-medium">GWT</span>
            )}
            {ac.priority && (
              <span className={clsx('text-[10px] px-1.5 py-0.5 rounded font-medium', getPriorityClass(ac.priority))}>
                {ac.priority}
              </span>
            )}
            {ac.effort && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-600 font-medium">{ac.effort}</span>
            )}
            {ac.origin === 'ai' && (
              <Sparkles className="w-3 h-3 text-accent-500" />
            )}
            {isEditedByUser && (
              <span title="Edited by user" className="text-amber-600">
                <Hand className="w-3 h-3" />
              </span>
            )}
          </div>

          {/* Content */}
          {isEditing ? (
            <div className="space-y-2">
              <textarea
                className="input text-sm resize-none w-full"
                rows={3}
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                autoFocus
              />
              <div className="flex gap-2">
                <button className="btn btn-primary btn-sm" onClick={handleSaveEdit}>Save</button>
                <button className="btn btn-secondary btn-sm" onClick={() => setIsEditing(false)}>Cancel</button>
              </div>
            </div>
          ) : isGWT ? (
            <div className="space-y-0.5 text-neutral-700">
              <p><span className="text-accent-600 font-medium">Given</span> {ac.given}</p>
              <p><span className="text-accent-600 font-medium">When</span> {ac.when}</p>
              <p><span className="text-accent-600 font-medium">Then</span> {ac.then}</p>
            </div>
          ) : (
            <p className="text-neutral-700 whitespace-pre-wrap">
              {ac.editedText || ac.text}
            </p>
          )}
          
          {/* Citations with Tooltip */}
          {ac.citationsUsed && ac.citationsUsed.length > 0 && (
            <div 
              className="relative inline-flex items-center gap-1 mt-1.5 text-[10px] text-accent-600 cursor-help"
              onMouseEnter={() => setShowCitationTooltip(true)}
              onMouseLeave={() => setShowCitationTooltip(false)}
            >
              <FileText className="w-3 h-3" />
              <span>{ac.citationsUsed.length} citations</span>
              
              {/* Citation Tooltip */}
              {showCitationTooltip && citationDetails.length > 0 && (
                <div className="absolute left-0 bottom-full mb-2 z-50 w-80 max-h-64 overflow-y-auto bg-white border border-neutral-200 rounded-lg shadow-lg p-3 text-left">
                  <p className="text-[10px] font-semibold text-neutral-500 uppercase tracking-wide mb-2">
                    Knowledge Base Citations
                  </p>
                  <div className="space-y-2">
                    {citationDetails.map((citation, i) => (
                      <div key={i} className="border-l-2 border-accent-300 pl-2">
                        <p className="font-medium text-neutral-800 text-xs truncate">
                          {citation?.title}
                        </p>
                        <p className="text-neutral-600 text-[11px] line-clamp-2 mt-0.5 italic">
                          "{citation?.quote}"
                        </p>
                        <span className={`text-[9px] mt-0.5 inline-block px-1 rounded ${
                          citation?.confidence === 'high' ? 'bg-green-100 text-green-700' :
                          citation?.confidence === 'medium' ? 'bg-amber-100 text-amber-700' :
                          'bg-neutral-100 text-neutral-600'
                        }`}>
                          {citation?.confidence}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            className="p-1 rounded hover:bg-neutral-100 text-neutral-400 hover:text-neutral-600 transition-colors"
            onClick={() => setIsEditing(true)}
            title="Edit"
          >
            <Edit3 className="w-3.5 h-3.5" />
          </button>
          <button
            className="p-1 rounded hover:bg-error-50 text-neutral-400 hover:text-error-500 transition-colors"
            onClick={onDelete}
            title="Delete"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Attachments Section
// ============================================

function AttachmentsSection({ 
  screenshots, 
  wireframes,
  attachments = [],
  onDeleteWireframe
}: { 
  screenshots: string[] | Screenshot[];
  wireframes: Array<{ id: string; imageUrl: string; createdAt?: string }>;
  attachments?: Attachment[];
  onDeleteWireframe?: (id: string) => void;
}) {
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  
  const wireframeAttachments = wireframes.map((wf, index) => ({
    id: wf.id,
    url: wf.imageUrl,
    type: 'wireframe' as const,
    createdAt: wf.createdAt,
    name: `Wireframe ${index + 1}`
  }));

  const screenshotAttachments = Array.isArray(screenshots)
    ? screenshots.map((s, i) =>
        typeof s === 'string'
          ? { id: `ss_${i}`, url: s, type: 'screenshot' as const }
          : {
              id: s.id,
              url: s.imageUrl || s.dataUrl || '',
              type: s.type || 'screenshot' as const,
              createdAt: s.capturedAt
            }
      )
    : [];

  const uploadImageAttachments = attachments
    .filter(att => att.type.startsWith('image/'))
    .map(att => ({
      id: att.id,
      url: att.dataUrl || att.url || '',
      type: 'upload' as const,
      name: att.name
    }));

  // Image attachments (screenshots + uploads)
  const imageAttachments = [...screenshotAttachments, ...uploadImageAttachments];

  // File attachments (non-image)
  const fileAttachments = attachments.filter(att => !att.type.startsWith('image/'));

  const totalCount = wireframeAttachments.length + imageAttachments.length + fileAttachments.length;

  const formatCreatedAt = (createdAt?: string) => {
    if (!createdAt) return 'Diagram';
    const date = new Date(createdAt);
    return Number.isNaN(date.getTime()) ? 'Diagram' : date.toLocaleString();
  };

  if (totalCount === 0) {
    return (
      <SectionCard>
        <SectionHeader title="Attachments" icon={Paperclip} count={0} />
        <p className="text-neutral-400 text-sm text-center py-3">No attachments</p>
      </SectionCard>
    );
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <>
      <SectionCard>
        <SectionHeader title="Attachments" icon={Paperclip} count={totalCount} />
        
        {/* Wireframes */}
        {wireframeAttachments.length > 0 && (
          <div className="space-y-2 mb-3">
            {wireframeAttachments.map((wf) => (
              <div
                key={wf.id}
                className="flex items-center gap-3 p-2 rounded-lg border border-neutral-200 bg-neutral-50 hover:bg-neutral-100 transition-colors"
              >
                <button
                  onClick={() => setPreviewImage(wf.url)}
                  className="w-10 h-10 rounded-md overflow-hidden border border-neutral-200 shrink-0"
                  title="Open wireframe"
                >
                  <img src={wf.url} alt={wf.name} className="w-full h-full object-cover" />
                </button>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-neutral-800 truncate">{wf.name}</div>
                  <div className="text-xs text-neutral-500 truncate">{formatCreatedAt(wf.createdAt)}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => setPreviewImage(wf.url)}
                    className="p-1 rounded hover:bg-white text-neutral-500 hover:text-neutral-700 transition-colors"
                    title="Open"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  {onDeleteWireframe && (
                    <button
                      onClick={() => onDeleteWireframe(wf.id)}
                      className="p-1 rounded hover:bg-error-50 text-neutral-500 hover:text-error-600 transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Image attachments */}
        {imageAttachments.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {imageAttachments.map((att) => (
              <button
                key={att.id}
                onClick={() => setPreviewImage(att.url)}
                className="relative group w-20 h-20 rounded-md overflow-hidden border border-neutral-200 hover:border-accent-400 transition-colors cursor-pointer"
              >
                <img
                  src={att.url}
                  alt={att.type}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Eye className="w-4 h-4 text-white" />
                </div>
                <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[9px] px-1 py-0.5 truncate">
                  {att.type}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* File attachments */}
        {fileAttachments.length > 0 && (
          <div className="space-y-1">
            {fileAttachments.map((file) => (
              <a
                key={file.id}
                href={file.dataUrl || file.url}
                download={file.name}
                className="flex items-center gap-2 p-2 rounded-md bg-neutral-50 hover:bg-neutral-100 transition-colors text-sm"
              >
                <FileOutput className="w-4 h-4 text-neutral-400" />
                <span className="flex-1 truncate text-neutral-700">{file.name}</span>
                <span className="text-neutral-400 text-xs">{formatFileSize(file.size)}</span>
              </a>
            ))}
          </div>
        )}
      </SectionCard>
      
      {/* Image Preview Modal */}
      {previewImage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={() => setPreviewImage(null)}
        >
          <button
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            onClick={() => setPreviewImage(null)}
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={previewImage}
            alt="Preview"
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}

// ============================================
// Citation Types (for ragBundle)
// ============================================

interface RAGCitation {
  citationId: string;
  doc: {
    docId: string;
    title: string;
    url?: string;
  };
  headingPath: string[];
  quote: {
    text: string;
  };
  whyRelevant: string;
  confidence: 'high' | 'medium' | 'low';
  rankScore: number;
  categories: string[];
  sourceType?: 'UPLOAD' | 'CONFLUENCE';
}

interface RAGBundle {
  citations: RAGCitation[];
  usedCitationIds: string[];
  metrics?: {
    reliability: number;
    coverage: number;
    completeness: number;
    citationsCount: number;
  };
}

// ============================================
// Citation Card Component
// ============================================

function CitationCard({ 
  citation, 
  isUsed 
}: { 
  citation: RAGCitation; 
  isUsed: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  
  const confidenceStyles = {
    high: 'bg-green-50 border-green-200',
    medium: 'bg-amber-50 border-amber-200',
    low: 'bg-neutral-50 border-neutral-200',
  };
  
  const confidenceBadgeStyles = {
    high: 'bg-green-100 text-green-700',
    medium: 'bg-amber-100 text-amber-700',
    low: 'bg-neutral-100 text-neutral-600',
  };
  
  return (
    <div 
      className={`p-3 rounded-lg border transition-all ${confidenceStyles[citation.confidence]} ${
        isUsed ? 'ring-1 ring-green-300' : ''
      }`}
    >
      <div className="flex items-start gap-2">
        {/* Icon based on source type */}
        <div className="flex-shrink-0 mt-0.5">
          {citation.sourceType === 'CONFLUENCE' ? (
            <Link2 className="w-4 h-4 text-blue-600" />
          ) : (
            <FileText className="w-4 h-4 text-purple-600" />
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          {/* Title and badges */}
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-medium text-sm text-neutral-800 truncate">
              {citation.doc.title}
            </p>
            {isUsed && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-[10px] font-medium">
                <CheckCircle className="w-3 h-3" />
                Used
              </span>
            )}
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${confidenceBadgeStyles[citation.confidence]}`}>
              {citation.confidence}
            </span>
          </div>
          
          {/* Heading path */}
          {citation.headingPath.length > 0 && (
            <p className="text-xs text-neutral-500 mt-0.5 truncate">
              {citation.headingPath.join(' > ')}
            </p>
          )}
          
          {/* Expandable content */}
          {expanded && (
            <div className="mt-2 space-y-2">
              {/* Quote */}
              <blockquote className="text-xs text-neutral-700 italic border-l-2 border-neutral-300 pl-2 py-1 bg-white/50 rounded-r">
                "{citation.quote.text}"
              </blockquote>
              
              {/* Why relevant */}
              <p className="text-xs text-neutral-600">
                <span className="font-medium">Relevance:</span> {citation.whyRelevant}
              </p>
              
              {/* Categories */}
              {citation.categories.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {citation.categories.map((cat, i) => (
                    <span 
                      key={i}
                      className="px-1.5 py-0.5 bg-neutral-100 text-neutral-600 rounded text-[10px]"
                    >
                      {cat}
                    </span>
                  ))}
                </div>
              )}
              
              {/* Link to document */}
              {citation.doc.url && (
                <a 
                  href={citation.doc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-accent-600 hover:underline"
                >
                  Open document
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          )}
        </div>
        
        {/* Expand/collapse button */}
        <button 
          onClick={() => setExpanded(!expanded)}
          className="flex-shrink-0 p-1 hover:bg-black/5 rounded transition-colors"
        >
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-neutral-500" />
          ) : (
            <ChevronDown className="w-4 h-4 text-neutral-500" />
          )}
        </button>
      </div>
    </div>
  );
}

// ============================================
// Citations Section
// ============================================

function CitationsSection({
  acceptanceCriteria,
  agentMeta
}: {
  acceptanceCriteria: EnhancedAC[];
  agentMeta: any;
}) {
  // Get ragBundle from agentMeta (stored during sync from extension)
  const ragBundle = agentMeta?.ragBundle as RAGBundle | undefined;
  
  // Filter to show only used citations
  const usedCitationIds = new Set(ragBundle?.usedCitationIds || []);
  
  // Also collect citation IDs from AC items
  acceptanceCriteria.forEach(ac => {
    ac.citationsUsed?.forEach(id => usedCitationIds.add(id));
  });
  
  // Get used citations with full data
  const usedCitations = ragBundle?.citations?.filter(
    c => usedCitationIds.has(c.citationId)
  ) || [];
  
  // Fallback: legacy ragSources (for backward compatibility)
  const ragSources = agentMeta?.ragSources || [];
  
  const totalCount = usedCitations.length + ragSources.length;
  
  if (totalCount === 0) {
    return null;
  }

  return (
    <SectionCard>
      <SectionHeader 
        title="Knowledge Base Citations" 
        icon={Book} 
        count={totalCount} 
      />
      
      {/* Used Citations with full data */}
      {usedCitations.length > 0 && (
        <div className="space-y-2">
          {ragBundle?.metrics && (
            <div className="flex items-center gap-3 text-xs text-neutral-500 mb-2">
              <span>Reliability: {Math.round((ragBundle.metrics.reliability || 0) * 100)}%</span>
              <span>Coverage: {Math.round((ragBundle.metrics.coverage || 0) * 100)}%</span>
            </div>
          )}
          {usedCitations.map(citation => (
            <CitationCard 
              key={citation.citationId} 
              citation={citation}
              isUsed={true}
            />
          ))}
        </div>
      )}
      
      {/* Legacy ragSources (backward compatibility) */}
      {ragSources.length > 0 && usedCitations.length === 0 && (
        <div className="space-y-1.5">
          <p className="text-[10px] text-neutral-500 tracking-wide mb-1">Knowledge Base Sources</p>
          <div className="space-y-1">
            {ragSources.map((source: any, i: number) => (
              <div 
                key={i}
                className="flex items-start gap-2 p-2 bg-neutral-50 border border-neutral-200 rounded-md text-xs"
              >
                <Book className="w-3 h-3 text-neutral-500 mt-0.5 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-neutral-800 truncate">{source.title || source.pageTitle || 'Untitled'}</p>
                  {source.pageUrl && (
                    <a 
                      href={source.pageUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accent-600 hover:underline flex items-center gap-1"
                    >
                      <span className="truncate">{source.pageUrl}</span>
                      <ExternalLink className="w-3 h-3 flex-shrink-0" />
                    </a>
                  )}
                  {source.relevanceScore && (
                    <span className="text-neutral-400">Score: {Math.round(source.relevanceScore * 100)}%</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </SectionCard>
  );
}

// ============================================
// Export to Confluence Modal
// ============================================

interface ConfluenceSpace {
  id: string;
  key: string;
  name: string;
  type: 'global' | 'personal';
  homepageId?: string;
  homepageTitle?: string;
}

interface ConfluencePage {
  id: string;
  title: string;
  path: string;
}

function ExportToConfluenceModal({
  projectId,
  storyId,
  storyTitle,
  onClose,
  onSuccess,
}: {
  projectId: string;
  storyId: string;
  storyTitle: string;
  onClose: () => void;
  onSuccess: (pageUrl: string) => void;
}) {
  const [spaces, setSpaces] = useState<ConfluenceSpace[]>([]);
  const [pages, setPages] = useState<ConfluencePage[]>([]);
  const [selectedSpaceKey, setSelectedSpaceKey] = useState<string>('');
  const [selectedParentId, setSelectedParentId] = useState<string>('');
  const [customTitle, setCustomTitle] = useState(`[Request] ${storyTitle}`);
  const [labels, setLabels] = useState<string>('ba-agent, requirement');
  const [loading, setLoading] = useState(true);
  const [loadingPages, setLoadingPages] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [sourceId, setSourceId] = useState<string>('');
  const [sourceName, setSourceName] = useState<string>('');

  // Load spaces on mount
  useEffect(() => {
    const loadSpaces = async () => {
      try {
        setLoading(true);
        setError('');
        const result = await api.get<{ data: ConfluenceSpace[]; sourceId?: string; sourceName?: string; message?: string }>(
          `/confluence-export/spaces?projectId=${projectId}`
        );
        
        if (result.message) {
          setError(result.message);
        } else {
          setSpaces(result.data || []);
          if (result.sourceId) setSourceId(result.sourceId);
          if (result.sourceName) setSourceName(result.sourceName);
          
          // Auto-select first space
          if (result.data && result.data.length > 0) {
            setSelectedSpaceKey(result.data[0].key);
          }
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load Confluence spaces');
      } finally {
        setLoading(false);
      }
    };
    
    loadSpaces();
  }, [projectId]);

  // Load pages when space changes
  useEffect(() => {
    if (!selectedSpaceKey) {
      setPages([]);
      return;
    }
    
    const loadPages = async () => {
      try {
        setLoadingPages(true);
        const result = await api.get<{ flatList: ConfluencePage[] }>(
          `/confluence-export/spaces/${selectedSpaceKey}/pages?projectId=${projectId}&sourceId=${sourceId}`
        );
        setPages(result.flatList || []);
      } catch (err: any) {
        console.error('Failed to load pages:', err);
        // Non-fatal - can still export to root
        setPages([]);
      } finally {
        setLoadingPages(false);
      }
    };
    
    loadPages();
  }, [selectedSpaceKey, projectId, sourceId]);

  const handleExport = async () => {
    if (!selectedSpaceKey) {
      setError('Please select a space');
      return;
    }
    
    try {
      setExporting(true);
      setError('');
      
      const result = await api.post<{ success: boolean; pageUrl: string; error?: string }>(
        '/confluence-export/export',
        {
          requestId: storyId,
          projectId,
          spaceKey: selectedSpaceKey,
          parentId: selectedParentId || undefined,
          title: customTitle,
          sourceId: sourceId || undefined,
          labels: labels.split(',').map(l => l.trim()).filter(Boolean),
        }
      );
      
      if (result.success) {
        onSuccess(result.pageUrl);
      } else {
        setError(result.error || 'Export failed');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to export to Confluence');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-neutral-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Book className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-neutral-900">Export to Confluence</h3>
              {sourceName && (
                <p className="text-xs text-neutral-500">Using: {sourceName}</p>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-neutral-100">
            <X className="w-5 h-5 text-neutral-400" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
              <span className="ml-2 text-neutral-600">Loading Confluence spaces...</span>
            </div>
          ) : error && spaces.length === 0 ? (
            <div className="p-4 bg-red-50 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-red-700 font-medium">Cannot connect to Confluence</p>
                  <p className="text-red-600 text-sm mt-1">{error}</p>
                  <p className="text-red-600 text-sm mt-2">
                    Make sure you have a Confluence knowledge source configured in the project's Knowledge Base settings.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Page Title */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Page Title
                </label>
                <input
                  type="text"
                  className="input w-full"
                  value={customTitle}
                  onChange={(e) => setCustomTitle(e.target.value)}
                  placeholder="Enter page title..."
                />
              </div>
              
              {/* Space Selection */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Confluence Space
                </label>
                <select
                  className="input w-full"
                  value={selectedSpaceKey}
                  onChange={(e) => {
                    setSelectedSpaceKey(e.target.value);
                    setSelectedParentId('');
                  }}
                >
                  <option value="">Select a space...</option>
                  {spaces.map((space) => (
                    <option key={space.key} value={space.key}>
                      {space.name} ({space.key})
                      {space.type === 'personal' ? ' (personal)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Parent Page Selection */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Parent Page <span className="text-neutral-400 font-normal">(optional)</span>
                </label>
                {loadingPages ? (
                  <div className="flex items-center gap-2 text-neutral-500 text-sm py-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading pages...
                  </div>
                ) : (
                  <select
                    className="input w-full"
                    value={selectedParentId}
                    onChange={(e) => setSelectedParentId(e.target.value)}
                    disabled={!selectedSpaceKey || pages.length === 0}
                  >
                    <option value="">Root level (no parent)</option>
                    {pages.map((page) => (
                      <option key={page.id} value={page.id}>
                        {page.path ? `${page.path} / ${page.title}` : page.title}
                      </option>
                    ))}
                  </select>
                )}
                <p className="text-xs text-neutral-500 mt-1">
                  The new page will be created under this parent page
                </p>
              </div>
              
              {/* Labels */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Labels <span className="text-neutral-400 font-normal">(comma-separated)</span>
                </label>
                <input
                  type="text"
                  className="input w-full"
                  value={labels}
                  onChange={(e) => setLabels(e.target.value)}
                  placeholder="ba-agent, requirement"
                />
              </div>
              
              {/* Error */}
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg text-red-700 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}
            </>
          )}
        </div>
        
        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-neutral-200 bg-neutral-50 rounded-b-lg">
          <button onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button
            onClick={handleExport}
            disabled={loading || exporting || !selectedSpaceKey || spaces.length === 0}
            className="btn btn-primary"
          >
            {exporting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Export to Confluence
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Main Component
// ============================================

export function StoryDetailPage() {
  const { orgId, projectId, epicId, storyId } = useParams<{
    orgId: string;
    projectId: string;
    epicId: string;
    storyId: string;
  }>();
  const queryClient = useQueryClient();
  const { setHeader, clearHeader } = usePageHeaderStore();
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [acFilterStatus, setAcFilterStatus] = useState<'approved' | 'all'>('approved');
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportedPageUrl, setExportedPageUrl] = useState<string | null>(null);
  
  const { data: _project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.get<ProjectInfo>(`/projects/${projectId}`),
    enabled: !!projectId,
  });
  
  const { data: story, isLoading } = useQuery({
    queryKey: ['story', storyId],
    queryFn: () => api.get<StoryDetail>(`/requests/${storyId}`),
    enabled: !!storyId,
  });
  
  // Set header when story data is loaded (must be before any early returns!)
  useEffect(() => {
    if (story) {
      const actionsList: Array<{
        id: string;
        label: string;
        icon: React.ReactNode;
        variant: 'primary' | 'secondary' | 'ghost' | 'danger';
        onClick: () => void;
      }> = [
        {
          id: 'export-confluence',
          label: 'Export to Confluence',
          icon: <Book className="w-4 h-4" />,
          variant: 'primary',
          onClick: () => setShowExportModal(true),
        },
      ];
      
      // Add "View in Confluence" if already exported
      if (exportedPageUrl) {
        actionsList.unshift({
          id: 'view-confluence',
          label: 'View in Confluence',
          icon: <ExternalLink className="w-4 h-4" />,
          variant: 'secondary',
          onClick: () => window.open(exportedPageUrl, '_blank'),
        });
      }
      
      setHeader(
        story.title,
        undefined,
        <FileText className="w-5 h-5" style={{ color: 'var(--primary)' }} />,
        <span className={`badge status-${story.status.toLowerCase().replace('_', '-')}`}>
          {story.status.replace('_', ' ')}
        </span>,
        {
          isEditable: true,
          onTitleChange: (newTitle) => {
            updateStoryMutation.mutate({ title: newTitle } as any);
          },
          actions: actionsList,
        }
      );
    }
    return () => clearHeader();
  }, [story, exportedPageUrl, setHeader, clearHeader]);
  
  const updateStoryMutation = useMutation({
    mutationFn: (data: Partial<StoryDetail>) =>
      api.patch(`/requests/${storyId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['story', storyId] });
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: (status: string) =>
      api.patch(`/requests/${storyId}`, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['story', storyId] });
      queryClient.invalidateQueries({ queryKey: ['story-comments', storyId] });
      setShowStatusDropdown(false);
    },
  });
  
  // AC Handlers
  const handleApproveAC = (acId: string) => {
    if (!story) return;
    const updatedAC = story.acceptanceCriteria.map(ac =>
      ac.id === acId ? { ...ac, reviewStatus: 'approved' as const } : ac
    );
    updateStoryMutation.mutate({ acceptanceCriteria: updatedAC } as any);
  };

  const handleRejectAC = (acId: string) => {
    if (!story) return;
    const updatedAC = story.acceptanceCriteria.map(ac =>
      ac.id === acId ? { ...ac, reviewStatus: 'draft' as const } : ac
    );
    updateStoryMutation.mutate({ acceptanceCriteria: updatedAC } as any);
  };

  const handleEditAC = (acId: string, text: string) => {
    if (!story) return;
    const updatedAC = story.acceptanceCriteria.map(ac =>
      ac.id === acId ? { ...ac, editedText: text, text: text, reviewStatus: 'edited' as const } : ac
    );
    updateStoryMutation.mutate({ acceptanceCriteria: updatedAC } as any);
  };

  const handleDeleteAC = (acId: string) => {
    if (!story) return;
    const updatedAC = story.acceptanceCriteria.filter(ac => ac.id !== acId);
    updateStoryMutation.mutate({ acceptanceCriteria: updatedAC } as any);
  };

  const handleDeleteWireframe = (wireframeId: string) => {
    if (!story) return;
    const updatedWireframes = story.wireframes.filter(wf => wf.id !== wireframeId);
    updateStoryMutation.mutate({ wireframes: updatedWireframes } as any);
  };

  // Scope update handler
  const handleScopeUpdate = (data: {
    preconditions?: TextItem[];
    trigger?: TextItem;
    businessRules?: TextItem[];
    outOfScope?: TextItem[];
  }) => {
    if (!story) return;
    const currentMeta = story.agentMeta || {};
    updateStoryMutation.mutate({
      agentMeta: {
        ...currentMeta,
        preconditions: data.preconditions ?? currentMeta.preconditions,
        trigger: data.trigger ?? currentMeta.trigger,
        businessRules: data.businessRules ?? currentMeta.businessRules,
        outOfScope: data.outOfScope ?? currentMeta.outOfScope,
      }
    } as any);
  };

  // Get scope data from agentMeta
  const agentMeta = story?.agentMeta || {};
  const preconditions = (agentMeta.preconditions || []) as TextItem[];
  const trigger = agentMeta.trigger as TextItem | undefined;
  const businessRules = (agentMeta.businessRules || []) as TextItem[];
  const outOfScope = (agentMeta.outOfScope || []) as TextItem[];
  const attachments = (agentMeta.attachments || []) as Attachment[];
  
  const approvedCount = story?.acceptanceCriteria.filter(ac => ac.reviewStatus === 'approved' || ac.reviewStatus === 'edited').length || 0;
  const totalAC = story?.acceptanceCriteria.length || 0;
  
  // Sort AC by priority (high priority first)
  const sortedAcceptanceCriteria = [...(story?.acceptanceCriteria || [])].sort((a, b) => {
    const priorityOrder: Record<string, number> = {
      'critical': 0, 'p0': 0,
      'must-have': 1, 'must': 1, 'p1': 1,
      'high': 2,
      'medium': 3, 'p2': 3,
      'should-have': 4, 'should': 4,
      'low': 5, 'p3': 5,
      'nice-to-have': 6, 'could': 6,
    };
    
    const getPriorityValue = (priority?: string) => {
      if (!priority) return 99;
      const normalized = priority.toLowerCase().replace(/[_\s]/g, '-');
      return priorityOrder[normalized] ?? 99;
    };
    
    return getPriorityValue(a.priority) - getPriorityValue(b.priority);
  });

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4 max-w-5xl mx-auto">
        <div className="h-8 bg-neutral-200 rounded w-1/3" />
        <div className="h-48 bg-neutral-200 rounded" />
        <div className="h-32 bg-neutral-200 rounded" />
      </div>
    );
  }
  
  if (!story) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
        <p className="text-neutral-600">Story not found</p>
        <Link 
          to={`/app/organizations/${orgId}/projects/${projectId}/epics/${epicId}`} 
          className="btn btn-secondary mt-4"
        >
          Back to Epic
        </Link>
      </div>
    );
  }
  
  return (
    <div className="max-w-5xl mx-auto space-y-4 animate-fade-in">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Main content - Left side */}
        <div className="lg:col-span-2 space-y-4">
          
          {/* Section 1: User Story */}
          <SectionCard>
            <div className="flex items-center justify-between mb-3">
              <SectionHeader title="User Story" icon={User} className="mb-0" />
              <Link
                to={`/app/organizations/${orgId}/projects/${projectId}/whiteboard?storyId=${storyId}`}
                className="btn btn-primary btn-sm"
              >
                <PenTool className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Wireframe</span>
              </Link>
            </div>
            <EditableUserStory
              userStory={story.userStory}
              onSave={(data) => updateStoryMutation.mutate({ userStory: data } as any)}
            />
          </SectionCard>

          {/* Section 3: Scope & Rules */}
          <ScopeRulesSection
            preconditions={preconditions}
            trigger={trigger}
            businessRules={businessRules}
            outOfScope={outOfScope}
            onUpdate={handleScopeUpdate}
          />
          
          {/* Section 4: Acceptance Criteria */}
          <SectionCard>
            <div className="flex items-center justify-between mb-3">
              <SectionHeader 
                title="Acceptance Criteria" 
                icon={CheckCircle2}
                count={`${approvedCount}/${totalAC}`}
              />
              
              {/* Filter toggle */}
              <div className="flex items-center gap-1 bg-neutral-100 rounded-lg p-0.5">
                <button
                  className={clsx(
                    'px-2.5 py-1 text-xs rounded-md transition-colors',
                    acFilterStatus === 'approved' 
                      ? 'bg-white text-neutral-800 shadow-sm' 
                      : 'text-neutral-500 hover:text-neutral-700'
                  )}
                  onClick={() => setAcFilterStatus('approved')}
                >
                  Approved
                </button>
                <button
                  className={clsx(
                    'px-2.5 py-1 text-xs rounded-md transition-colors',
                    acFilterStatus === 'all' 
                      ? 'bg-white text-neutral-800 shadow-sm' 
                      : 'text-neutral-500 hover:text-neutral-700'
                  )}
                  onClick={() => setAcFilterStatus('all')}
                >
                  All
                </button>
              </div>
            </div>
            
            {/* AC Config info */}
            {agentMeta.acConfig && (
              <div className="flex items-center gap-3 mb-3 text-xs text-neutral-500">
                <span className="flex items-center gap-1">
                  <Settings className="w-3 h-3" />
                  {agentMeta.acConfig.outputFormat}
                </span>
                <span>•</span>
                <span>{agentMeta.acConfig.framework}</span>
                {agentMeta.acConfig.includeRAG && (
                  <>
                    <span>•</span>
                    <span className="text-accent-600">RAG enabled</span>
                  </>
                )}
              </div>
            )}
            
            {(() => {
              // Filter: 'approved' includes 'edited' since it was previously approved
              const filteredAC = acFilterStatus === 'all' 
                ? sortedAcceptanceCriteria 
                : sortedAcceptanceCriteria.filter(ac => ac.reviewStatus === 'approved' || ac.reviewStatus === 'edited');
              
              return filteredAC.length > 0 ? (
                <div className="space-y-2">
                  {filteredAC.map((ac, i) => (
                    <ACItem
                      key={ac.id}
                      ac={ac}
                      index={i}
                      onApprove={() => handleApproveAC(ac.id)}
                      onReject={() => handleRejectAC(ac.id)}
                      onEdit={(text) => handleEditAC(ac.id, text)}
                      onDelete={() => handleDeleteAC(ac.id)}
                      ragBundle={(agentMeta as any)?.ragBundle}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-neutral-400 text-sm text-center py-4">
                  {acFilterStatus === 'approved' && totalAC > 0 
                    ? 'No approved criteria. Switch to "All" to see all items.' 
                    : 'No acceptance criteria yet'}
                </p>
              );
            })()}
          </SectionCard>
          
          {/* Section 6: Attachments */}
          <AttachmentsSection 
            screenshots={story.evidence.screenshots || []}
            wireframes={story.wireframes}
            attachments={attachments}
            onDeleteWireframe={handleDeleteWireframe}
          />
          
          {/* Section 7: Knowledge Base Citations */}
          <CitationsSection
            acceptanceCriteria={story.acceptanceCriteria}
            agentMeta={agentMeta}
          />
        </div>
        
        {/* Sidebar - Right side */}
        <div className="space-y-4">
          {/* Meta info */}
          <SectionCard>
            <div className="space-y-3 text-sm">
              {/* Status dropdown */}
              <div className="flex items-center gap-2 relative">
                <AlertCircle className="w-4 h-4 text-neutral-400" />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] text-neutral-500 tracking-wide">Status</p>
                  <button
                    className={clsx(
                      'badge px-2 py-0.5 cursor-pointer flex items-center gap-1 text-xs mt-0.5',
                      `status-${story.status.toLowerCase().replace('_', '-')}`
                    )}
                    onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                  >
                    {story.status.replace('_', ' ')}
                    <ChevronDown className="w-3 h-3" />
                  </button>
                  {showStatusDropdown && (
                    <div className="absolute left-0 mt-1 w-40 bg-white border border-neutral-200 rounded-md shadow-lg py-1 z-10">
                      {STATUS_OPTIONS.map((status) => (
                        <button
                          key={status}
                          className={clsx(
                            'w-full px-3 py-1.5 text-left text-xs hover:bg-neutral-50 transition-colors',
                            status === story.status ? 'text-accent-600 font-medium' : 'text-neutral-700'
                          )}
                          onClick={() => updateStatusMutation.mutate(status)}
                        >
                          {status.replace('_', ' ')}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {story.assignee && (
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-neutral-400" />
                  <div className="min-w-0">
                    <p className="text-[10px] text-neutral-500 tracking-wide">Assignee</p>
                    <p className="text-neutral-700 truncate">
                      {story.assignee.fullName || story.assignee.email}
                    </p>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-neutral-400" />
                <div className="min-w-0">
                  <p className="text-[10px] text-neutral-500 tracking-wide">Created by</p>
                  <p className="text-neutral-700 truncate">
                    {story.createdBy?.fullName || story.createdBy?.email || 'Unknown'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-neutral-400" />
                <div>
                  <p className="text-[10px] text-neutral-500 tracking-wide">Created</p>
                  <p className="text-neutral-700">
                    {new Date(story.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-neutral-400" />
                <div>
                  <p className="text-[10px] text-neutral-500 tracking-wide">Updated</p>
                  <p className="text-neutral-700">
                    {new Date(story.updatedAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
          </SectionCard>
          
          
          {/* Priority */}
          {story.priority && (
            <SectionCard>
              <SectionHeader title="Priority" icon={AlertCircle} />
              <span className={clsx(
                'badge',
                story.priority === 'CRITICAL' && 'badge-danger',
                story.priority === 'HIGH' && 'badge-warning',
                story.priority === 'MEDIUM' && 'badge-info',
                story.priority === 'LOW' && 'badge-neutral',
              )}>
                {story.priority}
              </span>
            </SectionCard>
          )}
        </div>
      </div>
      
      {/* Export to Confluence Modal */}
      {showExportModal && projectId && storyId && (
        <ExportToConfluenceModal
          projectId={projectId}
          storyId={storyId}
          storyTitle={story.title}
          onClose={() => setShowExportModal(false)}
          onSuccess={(pageUrl) => {
            setShowExportModal(false);
            setExportedPageUrl(pageUrl);
          }}
        />
      )}
    </div>
  );
}
